import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useOutletContext } from "react-router-dom";
import KnowledgeGapsDataTable from "./KnowledgeGapsDataTable";
import Pagination from "../../../components/Pagination/Pagination";
import ViewResponseModal from "./ViewResponseModal"; // IMPORT MODAL
import Cookies from "js-cookie";
import api from "../../../pages/chat/api"; 

const KnowledgeGapsTab = () => {
  const { searchQuery, orgId, columnPreferences, isLoadingColumns } = useOutletContext();
  const [selectedRows, setSelectedRows] = useState([]);
  
  const [tableData, setTableData] = useState([]);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  
  // Modal State
  const [modalData, setModalData] = useState(null);

  const [footerPortalNode, setFooterPortalNode] = useState(null);
  useEffect(() => {
    setFooterPortalNode(document.getElementById("analytics-footer-portal"));
  }, []);

  useEffect(() => {
    const fetchTableData = async () => {
      if (!orgId) {
        setTableData([]);
        return; 
      }

      setIsLoadingTable(true);
      try {
        const token = Cookies.get("access_token");
        const response = await api.get(`/api/v1/org/knowledge-gap/`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data && Array.isArray(response.data.results)) {
          const formattedData = response.data.results.map((item) => ({
            id: item.id || Math.random().toString(36).substr(2, 9), 
            timestamp: item.timestamp 
              ? new Date(item.timestamp).toLocaleString("en-US", {
                  month: "2-digit", day: "2-digit", year: "numeric", 
                  hour: "2-digit", minute: "2-digit", hour12: true
                }).replace(",", "") 
              : "-", 
            context: item.context || "-",
            user_query: item.user_query || item.query || "-",
            response: item.response || "-",
            chat_score: item.chat_score ?? item.score ?? 0 
          }));

          setTableData(formattedData);
        } else {
          setTableData([]);
        }
      } catch (error) {
        console.error("Error fetching knowledge gaps data:", error);
        setTableData([]);
      } finally {
        setIsLoadingTable(false);
      }
    };

    fetchTableData();
  }, [orgId]);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const filteredData = useMemo(() => {
    if (!searchQuery) return tableData;
    const query = searchQuery.toLowerCase();
    
    return tableData.filter(item => 
      (item.user_query && item.user_query.toLowerCase().includes(query)) || 
      (item.context && item.context.toLowerCase().includes(query))
    );
  }, [searchQuery, tableData]);

  const currentData = useMemo(() => {
    const indexOfLastItem = currentPage * rowsPerPage;
    const indexOfFirstItem = indexOfLastItem - rowsPerPage;
    return filteredData.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredData, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const handleSelectAll = (isChecked, e) => {
    if (isChecked) {
      setSelectedRows(currentData.map(d => d.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev => prev.includes(id) ? prev.filter(rId => rId !== id) : [...prev, id]);
  };

  const paginationUI = useMemo(() => {
    if (filteredData.length === 0) return null;
    return (
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={filteredData.length}
        rowsPerPage={rowsPerPage}
        onPageChange={setCurrentPage}
        onRowsPerPageChange={setRowsPerPage}
      />
    );
  }, [filteredData.length, currentPage, totalPages, rowsPerPage]);

  const isCombinedLoading = isLoadingTable || isLoadingColumns;

  return (
    <>
      <div className="max-w-full overflow-x-hidden flex flex-col h-full bg-white pt-2">
        <div className="flex flex-col flex-1 overflow-hidden w-full pl-2 pr-1 pb-2">
          <div className="flex-1 flex flex-col w-[99%] ml-1 overflow-hidden bg-white border border-black/10 rounded-lg">
            <div className="flex-1 overflow-auto custom-scrollbar">
              <KnowledgeGapsDataTable 
                data={currentData} 
                loading={isCombinedLoading} 
                selectedRows={selectedRows}
                onSelectAll={handleSelectAll}
                onSelectRow={handleSelectRow}
                columnPreferences={columnPreferences}
                onViewResponse={(item) => setModalData(item)} // PASS CLICK HANDLER
              />
            </div>
          </div>
        </div>
      </div>

      {footerPortalNode && createPortal(paginationUI, footerPortalNode)}

      <ViewResponseModal 
        isOpen={!!modalData} 
        onClose={() => setModalData(null)} 
        data={modalData} 
      />
    </>
  );
};

export default KnowledgeGapsTab;